import React from 'react'
import { Text } from '../../components/ui'
import { ClassificationLabel } from '../../components/ClassificationTrendIndicator'
import type { ComercialCupom } from '../../types/comercial'
import type {
  ComercialCockpitBrokerDetail,
  ComercialCockpitCampaignContext,
  ComercialCockpitCampaigns,
} from '../../types/comercialCockpit'
import type { ShopBroker } from '../../types/shop'
import {
  BATEU_LEVOU_GOAL,
  MINIMETAS_MILESTONES,
  REI_IMEDIATOS_MILESTONES,
  formatReferenceMonth,
  getAchievedMinimetasMilestones,
  getAchievedReiMilestones,
  getBateuLevouGoalStatus,
  getNextMinimetasMilestone,
  getNextReiMilestone,
  normalizeReferenceMonthKey,
} from '../../utils/campanhas2026Helpers'
import { formatCurrencyBRL, formatIntegerPtBr } from '../../utils/cotacaoFormatters'
import {
  BoltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FireIcon,
  RocketLaunchIcon,
  StarIcon,
  TrophyIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'

type CampaignSectionId =
  | 'minimetas'
  | 'campeao'
  | 'decole'
  | 'rei'
  | 'bateuLevou'
  | 'indicacoes'
  | 'pontuacao'
  | 'cupons'

interface CockpitCampaignsDetailTabProps {
  broker: ComercialCockpitBrokerDetail
  campaigns: ComercialCockpitCampaigns
  campaignContext: ComercialCockpitCampaignContext
  colorMap: Map<string, string | null>
  canViewValues: boolean
}

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <Text size="xs" color="muted">
      {label}
    </Text>
    <div className="text-gray-900 dark:text-white font-medium mt-0.5">{value}</div>
  </div>
)

function formatCupomValue(cupom: ComercialCupom): string {
  return cupom.discount_type === 'percent'
    ? `${Number(cupom.value)}%`
    : formatCurrencyBRL(Number(cupom.value))
}

function formatCupomDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function isCampaignActive(id: CampaignSectionId, campaigns: ComercialCockpitCampaigns, context: ComercialCockpitCampaignContext): boolean {
  switch (id) {
    case 'minimetas':
      return campaigns.minimetasQuantidade > 0
    case 'campeao':
      return campaigns.campeaoMes.length > 0
    case 'decole':
      return (
        campaigns.decoleVendas.some((e) => e.value > 0) ||
        campaigns.decoleProducao.some((e) => e.value > 0)
      )
    case 'rei':
      return campaigns.reiImediatosQuantidade > 0
    case 'bateuLevou':
      return !!(campaigns.bateuLevouJun || campaigns.bateuLevouOut)
    case 'indicacoes':
      return (
        (campaigns.indicacoesApp?.quantidade_total ?? 0) > 0 ||
        (campaigns.indicacoesFarma?.quantidade_total ?? 0) > 0
      )
    case 'pontuacao':
      return !!context.brokerPoints
    case 'cupons':
      return context.availableCupons.length > 0
    default:
      return false
  }
}

const CampaignAccordionSection: React.FC<{
  id: CampaignSectionId
  title: string
  icon: React.ReactNode
  active: boolean
  expanded: boolean
  onToggle: () => void
  children: React.ReactNode
}> = ({ title, icon, active, expanded, onToggle, children }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-900/40 transition-colors"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-primary-600 dark:text-primary-400 shrink-0">{icon}</span>
        <Text size="sm" className="font-semibold text-gray-900 dark:text-white truncate">
          {title}
        </Text>
        <span
          className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${
            active
              ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800'
              : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
          }`}
        >
          {active ? 'Participando' : 'Sem participação'}
        </span>
      </div>
      {expanded ? (
        <ChevronUpIcon className="h-5 w-5 text-gray-400 shrink-0" />
      ) : (
        <ChevronDownIcon className="h-5 w-5 text-gray-400 shrink-0" />
      )}
    </button>
    {expanded && (
      <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700/60">
        <div className="pt-4">{children}</div>
      </div>
    )}
  </div>
)

const PointsSection: React.FC<{ brokerPoints: ShopBroker | null }> = ({ brokerPoints }) => {
  if (!brokerPoints) {
    return (
      <Text size="sm" color="muted">
        Corretor sem cadastro no Shop - pontuação indisponível.
      </Text>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <DetailItem
        label="Saldo atual"
        value={
          <span
            className={
              brokerPoints.points_balance >= 0
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-600 dark:text-red-400'
            }
          >
            {brokerPoints.points_balance.toLocaleString('pt-BR')} PASI Points
          </span>
        }
      />
      <DetailItem
        label="PASI Points ganhos"
        value={brokerPoints.total_points_earned.toLocaleString('pt-BR')}
      />
      <DetailItem
        label="PASI Points gastos"
        value={brokerPoints.total_points_spent.toLocaleString('pt-BR')}
      />
    </div>
  )
}

const CockpitCampaignsDetailTab: React.FC<CockpitCampaignsDetailTabProps> = ({
  broker,
  campaigns,
  campaignContext,
  colorMap,
  canViewValues,
}) => {
  const [expandedId, setExpandedId] = React.useState<CampaignSectionId | null>('minimetas')

  const toggle = (id: CampaignSectionId) => {
    setExpandedId((current) => (current === id ? null : id))
  }

  const minimetasQty = campaigns.minimetasQuantidade
  const reiQty = campaigns.reiImediatosQuantidade
  const minimetasAchieved = getAchievedMinimetasMilestones(minimetasQty)
  const minimetasNext = getNextMinimetasMilestone(minimetasQty)
  const reiAchieved = getAchievedReiMilestones(reiQty)
  const reiNext = getNextReiMilestone(reiQty)

  const sortedCampeao = [...campaigns.campeaoMes].sort((a, b) =>
    normalizeReferenceMonthKey(a.reference_month).localeCompare(normalizeReferenceMonthKey(b.reference_month))
  )

  const sections: Array<{
    id: CampaignSectionId
    title: string
    icon: React.ReactNode
    content: React.ReactNode
  }> = [
    {
      id: 'minimetas',
      title: 'Minimetas 2026',
      icon: <StarIcon className="h-5 w-5" />,
      content:
        minimetasQty > 0 ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailItem label="Propostas" value={formatIntegerPtBr(minimetasQty)} />
              <DetailItem
                label="Próxima meta"
                value={
                  minimetasNext
                    ? `Faltam ${minimetasNext - minimetasQty} para ${minimetasNext}`
                    : 'Todas as metas atingidas ✓'
                }
              />
            </div>
            <div>
              <Text size="xs" color="muted" className="mb-1">
                Marcos
              </Text>
              <div className="flex flex-wrap gap-1">
                {MINIMETAS_MILESTONES.map((m) => (
                  <span
                    key={m}
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      minimetasAchieved.includes(m)
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                        : 'bg-gray-100 text-gray-400 dark:bg-gray-700'
                    }`}
                  >
                    {m} {minimetasAchieved.includes(m) ? '✓' : ''}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Text size="sm" color="muted">
            Sem registro nesta campanha.
          </Text>
        ),
    },
    {
      id: 'campeao',
      title: 'Campeão do Mês',
      icon: <TrophyIcon className="h-5 w-5" />,
      content:
        sortedCampeao.length > 0 ? (
          <div className="space-y-2">
            {sortedCampeao.map((entry) => (
              <div
                key={`${entry.reference_month}-${entry.campaign_id}`}
                className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-gray-200 dark:border-gray-700 last:border-0 pb-2 last:pb-0"
              >
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatReferenceMonth(entry.reference_month)}
                </span>
                {entry.value > 0 && (
                  <span className="text-amber-700 dark:text-amber-300 font-semibold">
                    {canViewValues ? formatCurrencyBRL(entry.value) : '—'}
                  </span>
                )}
                {entry.campaign_description && (
                  <Text size="xs" color="muted" className="w-full">
                    {entry.campaign_description}
                  </Text>
                )}
              </div>
            ))}
          </div>
        ) : (
          <Text size="sm" color="muted">
            Não foi campeão em nenhum mês registrado.
          </Text>
        ),
    },
    {
      id: 'decole',
      title: 'Decole com Influenciadores',
      icon: <RocketLaunchIcon className="h-5 w-5" />,
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Text size="xs" className="font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
              Vendas
            </Text>
            {campaignContext.decoleVendasRank ? (
              <div className="space-y-2">
                <DetailItem label="Posição" value={`${campaignContext.decoleVendasRank.position}°`} />
                {canViewValues && (
                  <DetailItem
                    label="Valor"
                    value={formatIntegerPtBr(campaignContext.decoleVendasRank.item.value)}
                  />
                )}
                {campaignContext.decoleVendasRank.item.campaign_description && (
                  <DetailItem
                    label="Campanha"
                    value={campaignContext.decoleVendasRank.item.campaign_description}
                  />
                )}
              </div>
            ) : (
              <Text size="sm" color="muted">
                Sem registro.
              </Text>
            )}
          </div>
          <div>
            <Text size="xs" className="font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
              Produção
            </Text>
            {campaignContext.decoleProducaoRank ? (
              <div className="space-y-2">
                <DetailItem label="Posição" value={`${campaignContext.decoleProducaoRank.position}°`} />
                {canViewValues && (
                  <DetailItem
                    label="Valor"
                    value={formatIntegerPtBr(campaignContext.decoleProducaoRank.item.value)}
                  />
                )}
                {campaignContext.decoleProducaoRank.item.campaign_description && (
                  <DetailItem
                    label="Campanha"
                    value={campaignContext.decoleProducaoRank.item.campaign_description}
                  />
                )}
              </div>
            ) : (
              <Text size="sm" color="muted">
                Sem registro.
              </Text>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'rei',
      title: 'Bateu, Levou! Imediatos',
      icon: <BoltIcon className="h-5 w-5" />,
      content:
        reiQty > 0 ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailItem label="Imediatos" value={formatIntegerPtBr(reiQty)} />
              <DetailItem
                label="Próximo marco"
                value={
                  reiNext
                    ? `Faltam ${reiNext - reiQty} para ${reiNext}`
                    : 'Meta máxima atingida ✓'
                }
              />
            </div>
            <div>
              <Text size="xs" color="muted" className="mb-1">
                Premiação
              </Text>
              <div className="flex flex-wrap gap-1">
                {REI_IMEDIATOS_MILESTONES.map((m) => (
                  <span
                    key={m}
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      reiAchieved.includes(m)
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                        : 'bg-gray-100 text-gray-400 dark:bg-gray-700'
                    }`}
                  >
                    {m} {reiAchieved.includes(m) ? '✓' : ''}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Text size="sm" color="muted">
            Sem registro nesta campanha.
          </Text>
        ),
    },
    {
      id: 'bateuLevou',
      title: 'Bateu Levou',
      icon: <FireIcon className="h-5 w-5" />,
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Período Junho', data: campaigns.bateuLevouJun },
            { label: 'Período Outubro', data: campaigns.bateuLevouOut },
          ].map(({ label, data }) => (
            <div key={label}>
              <Text size="xs" className="font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                {label}
              </Text>
              {data ? (
                <div className="space-y-2">
                  <DetailItem
                    label="Prêmio pago"
                    value={canViewValues ? formatCurrencyBRL(data.paid_premium) : '—'}
                  />
                  <DetailItem
                    label="Pendente"
                    value={canViewValues ? formatCurrencyBRL(data.pending_premium) : '—'}
                  />
                  <DetailItem
                    label="Total projetado"
                    value={
                      canViewValues
                        ? formatCurrencyBRL(data.paid_premium + data.pending_premium)
                        : '—'
                    }
                  />
                  <DetailItem
                    label={`Meta R$ ${BATEU_LEVOU_GOAL.toLocaleString('pt-BR')}`}
                    value={
                      canViewValues
                        ? getBateuLevouGoalStatus(data.paid_premium, formatCurrencyBRL)
                        : '—'
                    }
                  />
                </div>
              ) : (
                <Text size="sm" color="muted">
                  Sem registro.
                </Text>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'indicacoes',
      title: 'Indicações',
      icon: <UserPlusIcon className="h-5 w-5" />,
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'PASI Super APP', data: campaigns.indicacoesApp },
            { label: 'PASI Farma', data: campaigns.indicacoesFarma },
          ].map(({ label, data }) => (
            <div key={label}>
              <Text size="xs" className="font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                {label}
              </Text>
              {data ? (
                <div className="space-y-2">
                  <DetailItem label="Ativas" value={formatIntegerPtBr(data.quantidade_ativas)} />
                  <DetailItem label="Inativas" value={formatIntegerPtBr(data.quantidade_inativas)} />
                  <DetailItem label="Total" value={formatIntegerPtBr(data.quantidade_total)} />
                </div>
              ) : (
                <Text size="sm" color="muted">
                  Sem registro.
                </Text>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'pontuacao',
      title: 'Pontuação',
      icon: <StarIcon className="h-5 w-5" />,
      content: <PointsSection brokerPoints={campaignContext.brokerPoints} />,
    },
    {
      id: 'cupons',
      title: 'Cupons disponíveis',
      icon: <TrophyIcon className="h-5 w-5" />,
      content:
        campaignContext.availableCupons.length === 0 ? (
          <Text size="sm" color="muted">
            Nenhum cupom disponível.
          </Text>
        ) : (
          <ul className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
            {campaignContext.availableCupons.map((cupom) => (
              <li
                key={cupom.id}
                className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm"
              >
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">{cupom.code}</span>
                  <Text size="xs" color="muted" className="mt-0.5">
                    {cupom.discount_type === 'percent' ? 'Percentual' : 'Valor fixo'} · Validade:{' '}
                    {formatCupomDate(cupom.valid_until)}
                  </Text>
                </div>
                <span className="font-medium text-emerald-700 dark:text-emerald-300 shrink-0">
                  {formatCupomValue(cupom)}
                </span>
              </li>
            ))}
          </ul>
        ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <Text size="sm" className="font-semibold text-gray-900 dark:text-white mb-2">
          Identificação nas campanhas
        </Text>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <DetailItem label="Nome" value={broker.name} />
          <DetailItem label="Código SCGP" value={broker.id_scgp ?? '—'} />
          <DetailItem label="Comercial responsável" value={broker.comercial_name ?? '—'} />
          <DetailItem label="UF" value={broker.state?.trim() || '—'} />
          <DetailItem
            label="Categoria"
            value={
              broker.classification ? (
                <ClassificationLabel classification={broker.classification} colorMap={colorMap} />
              ) : (
                '—'
              )
            }
          />
        </div>
      </div>

      <Text size="sm" color="muted">
        Clique em cada campanha para ver os detalhes completos.
      </Text>

      <div className="space-y-3">
        {sections.map((section) => (
          <CampaignAccordionSection
            key={section.id}
            id={section.id}
            title={section.title}
            icon={section.icon}
            active={isCampaignActive(section.id, campaigns, campaignContext)}
            expanded={expandedId === section.id}
            onToggle={() => toggle(section.id)}
          >
            {section.content}
          </CampaignAccordionSection>
        ))}
      </div>
    </div>
  )
}

export default CockpitCampaignsDetailTab
